import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// 30 s default timeout — large enough for slow mobile networks but
// short enough to surface a clear toast instead of an indefinite spinner.
const api = axios.create({ baseURL: API, timeout: 30000 });

// One-shot transparent retry on network-level failures (offline tab,
// flaky 3G/4G, DNS hiccup, intermittent CORS preflight). Server errors
// (4xx/5xx with a response) are NEVER retried — they bubble up as-is.
api.interceptors.request.use((config) => {
    config._retry = config._retry || 0;
    return config;
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("discover_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

/**
 * Normalize whatever shape the backend (or FastAPI auto-422) puts under
 * `detail` into a single string safe to feed to <toast />, JSX, etc.
 *
 * FastAPI/Pydantic v2 returns `detail: [{ type, loc, msg, input, ctx }, ...]`
 * for request validation errors. Rendering that array directly in JSX
 * triggers "Objects are not valid as a React child".
 */
function normalizeDetail(detail) {
    if (!detail) return null;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        const msgs = detail.map((d) => {
            if (!d || typeof d !== "object") return String(d ?? "");
            // Pydantic-shaped validation error
            if (d.msg) {
                const field = Array.isArray(d.loc) && d.loc.length > 1
                    ? d.loc.slice(1).join(".")
                    : null;
                return field ? `${field} : ${d.msg}` : d.msg;
            }
            try { return JSON.stringify(d); } catch { return String(d); }
        }).filter(Boolean);
        return msgs.join(" · ") || null;
    }
    if (typeof detail === "object") {
        if (detail.msg) return detail.msg;
        try { return JSON.stringify(detail); } catch { return null; }
    }
    return String(detail);
}

api.interceptors.response.use(
    (r) => r,
    async (e) => {
        // Retry once on pure network errors (no response received).
        // Identified via Axios' `code === "ERR_NETWORK"` or no `e.response`.
        const cfg = e.config;
        const isNetworkErr = !e.response && (e.code === "ERR_NETWORK" || e.message === "Network Error" || e.code === "ECONNABORTED");
        // iter55 — Also retry once on transient 403 with no JSON body.
        // Cloudflare's Bot Fight Mode / challenge system occasionally
        // returns 403 with an HTML challenge page (no `detail`) — the
        // second attempt with the same headers almost always passes.
        // We DON'T retry 403 with an application-level `detail` field
        // (e.g. "Compte banni", "Reserve admin") because those aren't
        // going to change on retry.
        const isEdgeBlock403 = e.response?.status === 403
            && (!e.response?.data || typeof e.response.data !== "object" || !e.response.data.detail);
        // iter56 — Cloudflare origin errors (520/521/522/523/524) are
        // ALWAYS transient CDN/origin edge issues (empty response,
        // origin timeout, connection reset). Retry once with backoff
        // to smooth over the hiccup instead of crashing the UI.
        const isEdge5xx = e.response?.status >= 520 && e.response?.status <= 524;
        // iter58 — Bumped edge-5xx retry cap to 2 (was 1) so users
        // survive back-to-back CDN blips (observed intermittent 520 in
        // prod even after our anti-crash exception handler shipped —
        // this catches the second-hop failure that used to bubble to
        // the toast).
        const maxRetries = isEdge5xx ? 2 : 1;
        if (cfg && (isNetworkErr || isEdgeBlock403 || isEdge5xx) && (cfg._retry || 0) < maxRetries) {
            cfg._retry = (cfg._retry || 0) + 1;
            // Short backoff so we don't pile up on a single flaky packet
            // or a still-warm CDN challenge. For 5xx we back off longer
            // and exponentially so a struggling origin has time to recover.
            const wait = isEdge5xx
                ? (1500 * cfg._retry)                      // 1.5s then 3s
                : (isEdgeBlock403 ? 1200 : 600);
            await new Promise((r) => setTimeout(r, wait));
            try { return await api.request(cfg); } catch (re) { e = re; }
        }
        if (e.response?.status === 401) {
            localStorage.removeItem("discover_token");
        }
        // Coerce error.response.data.detail to a string so that
        // `toast.error(err.response.data.detail)` never renders an object.
        const data = e.response?.data;
        if (data && typeof data === "object") {
            const norm = normalizeDetail(data.detail);
            if (norm !== null) data.detail = norm;
        }
        // iter55 — When the edge (Cloudflare / Kubernetes ingress) returns
        // a 403 with no JSON body, replace the raw axios "Request failed
        // with status code 403" message by something the user can act on.
        if (e.response?.status === 403
                && (!data || typeof data !== "object" || !data.detail)) {
            e._friendly = "Blocage temporaire — réessaie dans un instant.";
        }
        // iter56 — Same friendly-message treatment for Cloudflare origin
        // errors (520 Origin Error, 521 Web Server Down, 522 Timeout,
        // 523 Origin Unreachable, 524 Timeout > 100s). The retry above
        // already covered the transient case; this handles the case
        // where the retry ALSO failed, so we surface a clear message
        // instead of the raw axios error string.
        if (e.response?.status >= 520 && e.response?.status <= 524) {
            e._friendly = "Le serveur met un instant à répondre. Réessaie dans quelques secondes.";
        }
        return Promise.reject(e);
    }
);

export default api;
